import { db, auth } from './firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { inventoryService as firestoreInventoryService } from './firestore';

/**
 * Servizi per l'interazione con Firestore Database
 * Struttura del database:
 * /users/{userId}/
 *   - devices/
 *   - inventory/
 * /settings/{userId}/
 */

// Servizi per i dispositivi
export const deviceService = {
  async create(data) {
    try {
      const userId = auth.currentUser.uid;
      const devicesRef = collection(db, `users/${userId}/devices`);
      const docRef = await addDoc(devicesRef, {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return { data: { id: docRef.id, ...data } };
    } catch (error) {
      console.error('Error creating device:', error);
      throw error;
    }
  },

  async getAll(params = {}) {
    try {
      const userId = auth.currentUser.uid;
      const devicesRef = collection(db, `users/${userId}/devices`);
      
      let q = query(devicesRef);
      
      if (params.status) {
        q = query(q, where('status', '==', params.status));
      }
      
      q = query(q, orderBy('createdAt', 'desc'));
      
      const snapshot = await getDocs(q);
      const devices = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return { data: devices };
    } catch (error) {
      console.error('Error fetching devices:', error);
      throw error;
    }
  },

  async update(id, data) {
    try {
      const userId = auth.currentUser.uid;
      const deviceRef = doc(db, 'users', userId, 'devices', id);
      await updateDoc(deviceRef, {
        ...data,
        updatedAt: new Date()
      });
      return { data: { id, ...data } };
    } catch (error) {
      console.error('Error updating device:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const userId = auth.currentUser.uid;
      const deviceRef = doc(db, 'users', userId, 'devices', id);
      await deleteDoc(deviceRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting device:', error);
      throw error;
    }
  },

  async getStatistics() {
    try {
      const userId = auth.currentUser.uid;
      const devicesRef = collection(db, 'users', userId, 'devices');
      const snapshot = await getDocs(devicesRef);
      
      const stats = {
        total: snapshot.size,
        inRiparazione: 0,
        completati: 0,
        consegnati: 0,
        ordini: 0
      };
      
      snapshot.docs.forEach(doc => {
        const device = doc.data();
        if (device.status === 'inRiparazione') stats.inRiparazione++;
        if (device.status === 'completato') stats.completati++;
        if (device.status === 'consegnato') stats.consegnati++;
        if (device.isOrder) stats.ordini++;
      });
      
      return { data: stats };
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }
};

// Servizi per l'inventario
export const inventoryService = {
  getAll: async () => {
    try {
      const userId = auth.currentUser.uid;
      const inventoryRef = collection(db, `users/${userId}/inventory`);
      const snapshot = await getDocs(inventoryRef);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching inventory:', error);
      throw error;
    }
  },
  
  create: async (data) => {
    try {
      return await firestoreInventoryService.create(data);
    } catch (error) {
      console.error('Error creating inventory item:', error);
      throw error;
    }
  },
  
  update: async (id, data) => {
    try {
      return await firestoreInventoryService.update(id, data);
    } catch (error) {
      console.error('Error updating inventory item:', error);
      throw error;
    }
  },
  
  delete: async (id) => {
    try {
      return await firestoreInventoryService.delete(id);
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      throw error;
    }
  }
};

// Definizione della struttura del documento del dispositivo
const deviceSchema = {
  brand: String,
  model: String,
  serialNumber: String,
  customerPhone: String,
  problem: String,
  notes: String,
  price: Number,
  deposit: Number,
  paymentStatus: String,
  isOrder: Boolean,
  pattern: Array, // Array di numeri che rappresentano il pattern
  deliveryDate: Timestamp,
  status: String,
  repairNumber: String,
  entryDate: Timestamp,
  // ... altri campi ...
};

// Funzione di validazione del pattern
const validatePattern = (pattern) => {
  return Array.isArray(pattern) && 
         pattern.every(num => Number.isInteger(num) && num >= 0 && num < 9);
};

// Funzione per salvare il dispositivo
export const saveDevice = async (deviceData) => {
  const pattern = deviceData.pattern || [];
  if (!validatePattern(pattern)) {
    throw new Error('Pattern non valido');
  }

  const formattedData = {
    ...deviceData,
    pattern: pattern,
    updatedAt: serverTimestamp(),
  };

  const userId = auth.currentUser.uid;
  const deviceRef = doc(db, `users/${userId}/devices/${deviceData.id}`);
  return await updateDoc(deviceRef, formattedData);
};

export default { deviceService, inventoryService }; 